import storage from "./storage/storage";
import rison from "rison";
import { getDatabaseNameFromPath } from "./uri";
import Context from "../app.context";
import { DataActions } from "../store/data/actions";
import { ICredentials } from "./credentialsStore";
import { DriveStore } from "../store/reducers";
import IndexedDbMigrationFromRedux from "./storage/indexedDbMigrationFromRedux";
import { IndexedDbStorage } from "./storage/indexedDbStorage";

const allowNumberedDomains: string[] = ["testDomain"];
const allowNumberedSubDomains: string[] = ["testSubDomain"];
const allowNumberedPrefix: string[] = [
    "testPrefix",
    "myapp",
    "gcemygeotp",
    "mirror",
    "supportapp",
    "alphaapp",
    "mirrorapp",
    "mystatic",
    "gcemygsbxp",
    "myapi",
    "support"
];
const doubleZones = ["com", "org", "net", "edu", "ngo", "gob"];

function cleanServer(server) {
    return server.replace(/\S*:\/\//, "").replace(/\/$/, "");
}

function getBasePage(database?: string, defaultOnly?: boolean) {
    let basePage = "default.html";
    if (!defaultOnly && window.location.pathname.indexOf("/drive/default-legacy.html") > -1) {
        basePage = "default-legacy.html";
    }
    basePage = `/drive/${ basePage }`;

    if (database) {
        basePage = `/${ database }${ basePage }`;
    }
    return basePage;
}

/**
 * Redirect to a new server (e.g. alpha.geotab.com).
 * @param server The server to redirect to
 * @param params Parameters to be rison encoded in hash
 * @param dontClear Whether to clear all localStorage before redirecting. Will clear by default.
 */
export async function redirectBase(server: string, params: any, dontClear: boolean) {
    if (!dontClear) {
        // Make sure local storage is clean before leaving this domain. Don't want weird stuff to happen later.
        if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            await IndexedDbStorage.clear();
        } else {
            storage.clear();
        }
    }
    let baseUrl = `${ window.location.protocol }//${ server }`;
    window.location.replace(`${ baseUrl }${ getBasePage(undefined, true) }#ui/login${ (params ? "," + rison.encode(params) : "") }`);
}

export function databaseRedirect(database: string) {
    window.__drive_redirect = true;
    let basePage = getBasePage(database);
    window.location.replace(window.location.href.replace(window.location.pathname, basePage));
}

export function loginRedirect(server: string, credentials: ICredentials, nextPage?: string | null, skipWorkflow?: boolean) {
    let cleanedServer = cleanServer(server);
    let risonObj: any = {
        server: cleanedServer,
        credentials: credentials,
        next: <any>null
    };
    if (nextPage) {
        risonObj.next = nextPage;
    }
    if (skipWorkflow) {
        risonObj.skipWorkflow = true;
    }
    redirectBase(cleanedServer, risonObj, false);
}

export function checkRedirectRequired(context: Context) {
    let driverCredentials = context && context.api && context.api.getCurrentCredentials();
    let dbFromCredentials = driverCredentials && driverCredentials.credentials && driverCredentials.credentials.database && driverCredentials.credentials.database.toLowerCase();
    if (dbFromCredentials && dbFromCredentials !== getDatabaseNameFromPath()) {
        return true;
    }

    return false;
}

export function redirectDatabaseIfNeeded(context: Context) {
    let driverCredentials = context && context.api && context.api.getCurrentCredentials();
    let dbFromCredentials = driverCredentials && driverCredentials.credentials && driverCredentials.credentials.database && driverCredentials.credentials.database.toLowerCase();
    if (dbFromCredentials && dbFromCredentials !== getDatabaseNameFromPath()) {
        let lastDb = (context.store.getState().data.lastDatabase || "").toLowerCase();
        if (!lastDb || lastDb !== dbFromCredentials) {
            context.store.dispatch(DataActions.setLastDatabase(dbFromCredentials));
        }
        databaseRedirect(dbFromCredentials);
        return true;
    }
    return false;
}

export function checkIfRedirectIsRequired(database: string, store: DriveStore) {
    let cleanDatabase = database.toLowerCase();
    if (cleanDatabase !== getDatabaseNameFromPath()) {
        let lastDb = (store.getState().data.lastDatabase || "").toLowerCase();
        if (!lastDb || lastDb !== cleanDatabase) {
            store.dispatch(DataActions.setLastDatabase(cleanDatabase));
        }
        return true;
    }
    return false;
}

export function isNumberFromSubDomainPathNotAllowed(vendorPrefix: IVendorPrefix) {
    if (!vendorPrefix.prefixNumber
        || allowNumberedPrefix.indexOf(vendorPrefix.prefix) > -1
        || allowNumberedDomains.indexOf(vendorPrefix.domain) > -1
        || allowNumberedSubDomains.indexOf(vendorPrefix.subDomain) > -1) {
        return false;
    }

    return true;
}

export function removeNumberFromSubDomainPath(vendorPrefix: IVendorPrefix) {
    if (!isNumberFromSubDomainPathNotAllowed(vendorPrefix)) {
        return;
    }

    if (!vendorPrefix.subDomain) {
        window.location.host = `${ vendorPrefix.prefix }.${ vendorPrefix.domain }.${ vendorPrefix.zone }`;
    } else {
        window.location.host = `${ vendorPrefix.prefix }.${ vendorPrefix.subDomain }.${ vendorPrefix.domain }.${ vendorPrefix.zone }`;
    }
}

export function getUrlPrefix(parts) {
    let prefix = {
        prefix: "",
        prefixNumber: "",
        domain: "",
        subDomain: "",
        zone: ""
    };

    if (parts.length > 1) {
        let zone = parts.pop() || "";
        let domain = parts.pop() || "";

        // zone name consist of two parts .com.uk
        if (doubleZones.indexOf(domain) >= 0) {
            zone = `${ domain }.${ zone }`;
            domain = parts.pop() || "";
        }

        if (isNaN(+zone) && isNaN(+domain)) {
            prefix.zone = zone;
            prefix.domain = domain;

            let urlPrefix = parts.shift() || "";
            prefix.prefixNumber = (urlPrefix.match(/\d+$/i) || [""]).shift() || "";
            prefix.prefix = urlPrefix.replace(prefix.prefixNumber, "");

            prefix.subDomain = parts.length > 0 ? parts.join("") : "";
        }
    }

    return prefix;
}